import './About.css'
import { HiddenDevComment } from './DeveloperMode'
import { devComments } from '../config/devComments'
import { useDebug } from '../contexts/DebugContext'
import { useRef, useState, useEffect } from 'react'

const skillCategories = [
  {
    title: "Backend & AI",
    skills: ["Java", "Spring Boot", "Microservices", "REST APIs", "Agentic AI", "LLM Orchestration", "SQL/NoSQL"]
  },
  {
    title: "Game Development",
    skills: ["Unity", "C#", "C++", "WebGL", "Asset Bundles", "Shader Lab", "Multiplayer Bots"]
  },
  {
    title: "Web & Tools",
    skills: ["React JS", "TypeScript", "HTML5/CSS3", "JavaScript", "Git", "Docker", "AWS", "Firebase"]
  }
]

const About = () => {
  const { config: debug } = useDebug()
  const sectionRef = useRef<HTMLElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  const animEnabled = debug.ANIMATIONS && debug.ANIM_SECTIONS?.ABOUT

  const [sectionInView, setSectionInView] = useState(!animEnabled)
  const [cardsVisible, setCardsVisible] = useState<Record<number, boolean>>({})

  useEffect(() => {
    if (!animEnabled) return
    const el = sectionRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSectionInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [animEnabled])

  useEffect(() => {
    if (!animEnabled) return
    const observers = skillCategories.map((_, index) => {
      const el = cardRefs.current[index]
      if (!el) return null
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setCardsVisible(prev => ({ ...prev, [index]: true }))
            observer.disconnect()
          }
        },
        { threshold: 0.15 }
      )
      observer.observe(el)
      return observer
    })
    return () => observers.forEach(o => o?.disconnect())
  }, [animEnabled])

  const sectionClass = animEnabled
    ? `about-section-${sectionInView ? 'visible' : 'hidden'}`
    : ''

  const textStyle = sectionInView
    ? { transitionDelay: '0.1s' }
    : undefined

  return (
    <section ref={sectionRef} id="about" className="about">
      <div className="container">
        <h2 className={sectionClass}>
          About Me
          <HiddenDevComment {...devComments.about} />
        </h2>

        <div className="about-content">
          <div className={`about-text ${sectionClass}`} style={textStyle}>
            <p>
              I am a results-driven <span>Senior Software Engineer</span> with 4.5+ years of professional experience. My philosophy is simple:
              <em> "A comfort zone is a beautiful place, but nothing ever grows there."</em> This mindset has driven me to build across the entire stack . from crafting engaging mobile games and immersive experiences to architecting highly scalable, complex multiplayer backend systems.
            </p>
            <p>
              I work across <span>full-stack web technologies (Java, Spring Boot, React) and game systems (Unity, C#)</span>, delivering high-impact solutions at scale. At MadOverGames, I led advanced PvP systems and AI development . building real-time matchmaking algorithms and optimizing bots for human-like behavior with <span>7-second level completion</span> . contributing to titles with <span>25M+ downloads and 4M+ active users</span>.
            </p>
            <p>
              In my most recent role at Extramarks, I took full ownership of 3 major systems and shipped them in 6 months as the solo engineer - building immersive gamified learning tools that redefined classroom engagement. Beyond writing clean and efficient code, I am deeply passionate about community building, having mentored students through <span>UnSaid Talks</span> and celebrated regional heritage with <span>VIRSA</span>.
            </p>
            {/* <div className="languages-tag-cloud">
              <span className="lang-chip">English</span>
              <span className="lang-chip">Hindi</span>
              <span className="lang-chip">Punjabi</span>
            </div> */}
          </div>

          <div className="skills-grid">
            {skillCategories.map((category, index) => {
              const cardHidden = animEnabled && !cardsVisible[index]
              return (
                <div
                  key={category.title}
                  ref={el => { cardRefs.current[index] = el }}
                  className={`skill-category ${cardHidden ? 'about-card-hidden' : ''} ${cardsVisible[index] ? 'about-card-visible' : ''}`}
                >
                  <h3>{category.title}</h3>
                  <div className="skill-tags">
                    {category.skills.map((skill) => (
                      <span key={skill} className="skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default About
